import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Mic, Calendar, Share2, Check, Sparkles } from 'lucide-react';
import ProgressButton from './components/ProgressButton';
import FloatingMascot from './components/FloatingMascot';
import BackgroundTetris from './components/BackgroundTetris';

interface ProgressData {
  totalMessages: number;
  conversationCount: number;
  totalDaysPracticing: number;
  weeklyStats: {
    messages: number;
    daysActive: number;
    estimatedMinutes: number;
  };
  estimatedMinutesTotal: number;
  /** Conquistas derivadas das conversas reais (backend); fallback para getAchievements se vazio */
  achievementsFromConversations?: string[];
  /** true quando não praticou nada na semana (min/dias = 0); Próximo passo mostra incentivo */
  noPracticeThisWeek?: boolean;
  /** Desafio ou incentivo para o próximo passo (backend); baseado em dados reais */
  nextStepChallenge?: { title: string; subtitle: string };
  /** Quando o backend enviar: % do tempo no modo advanced (0-100) */
  advancedUsagePercent?: number;
  /** Quando o backend enviar: true se usa APENAS modo advanced */
  useOnlyAdvanced?: boolean;
  /** Quando o backend enviar: média de "perguntas de significado" ou "não entendi" por sessão */
  asksMeaningsPerSession?: number;
  /** Quando o backend enviar: quantos dias na semana atual com pelo menos 15 min de uso */
  daysWith15MinThisWeek?: number;
}

interface ProgressProps {
  data: ProgressData;
  userName: string;
}

// Determine achievements based on real data
function getAchievements(data: ProgressData): string[] {
  const achievements: string[] = [];
  if (data.totalMessages >= 3) achievements.push('Se apresentar em inglês');
  if (data.totalMessages >= 15) achievements.push('Falar sobre sua rotina');
  if (data.totalMessages >= 30) achievements.push('Manter uma conversa simples');
  if (data.totalMessages >= 50) achievements.push('Expressar opiniões em inglês');
  if (data.totalMessages >= 100) achievements.push('Conversar sobre temas variados');
  return achievements.length > 0 ? achievements : ['Começou a falar em inglês'];
}

// Número da semana no ano (1–53) para rotação semanal de copy
function getWeekOfYear(date: Date = new Date()): number {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7; // Segunda = 1
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}

// Variações semanais de dica por nível (mesmo significado, tom encorajador, sem repetição literal)
const TIPS_BASICO: string[] = [
  'O próximo nível não pede perfeição — pede tentativa. Fique mais tempo no modo Advanced, arrisque falar sem tanta ajuda e, quando precisar, use os ebooks de apoio pra ganhar confiança e voltar pra conversa. Cada tentativa te aproxima da fluência!',
  'Fluência começa com tentativa, não com acerto. Que tal passar mais tempo no Advanced? Se travar, os ebooks estão aí pra te apoiar. O importante é se soltar — cada conversa conta.',
  'Ninguém sobe de nível esperando estar pronto. Arrisque mais no modo Advanced, use os ebooks quando precisar de um apoio, e deixe o erro fazer parte do jogo. É assim que a confiança cresce.',
  'O próximo passo é tentar mais, não acertar mais. Fique mais no Advanced, solte-se na conversa e recorra aos ebooks quando quiser. Cada vez que você fala, ganha terreno.',
  'A passagem pro próximo nível é tentativa, não perfeição. Mais tempo no Advanced, sem medo de errar, e com os ebooks por perto quando precisar. Cada fala te deixa mais solto.',
  'O jogo premia quem tenta, não quem espera estar pronto. Arrisque no Advanced, use os ebooks pra se soltar e volte pra conversa. Cada tentativa te aproxima da fluência.',
];

const TIPS_INTERMEDIARIO: string[] = [
  'O jogo agora é constância, não esforço. Fique 100% no modo Advanced e transforme o inglês em hábito real: algumas conversas por semana já fazem diferença. Quinze minutos são suficientes quando você fala de verdade. Não precisa ser todo dia, só não pode parar. Quanto mais natural isso fica, menos você percebe — e é aí que o fluente começa a aparecer.',
  'Agora o que pesa é ritmo, não quantidade. Use só o Advanced e deixe o inglês entrar na rotina: poucas conversas por semana bastam. Quando for de verdade, pouco tempo já conta. O fluente aparece quando isso vira natural.',
  'O diferencial daqui pra frente é manter, não intensificar. Advanced o tempo todo, inglês como hábito — algumas vezes por semana já mudam o jogo. Sem pressão de todo dia; o que importa é não parar. Naturalidade é o sinal de que está entrando no fluente.',
  'Daqui em diante, constância ganha de esforço. Fique no Advanced, faça do inglês um hábito leve: conversas regulares na semana já ajudam. Não é sobre fazer todo dia; é sobre não deixar esfriar. Quando ficar natural, o fluente aparece.',
  'O passo agora é virar hábito, não maratona. 100% Advanced, inglês com regularidade — algumas conversas por semana já fazem diferença. Não precisa ser diário; precisa ser constante. O fluente surge quando você nem percebe.',
  'Agora o jogo é manter o ritmo. Advanced o tempo todo, inglês como hábito real: conversas frequentes na semana já contam. Sem cobrança de todo dia — o que importa é não parar. O fluente começa a aparecer quando isso vira natural.',
];

const TIPS_FLUENTE: string[] = [
  'Agora o inglês faz parte de você. Continue usando o Advanced sem pensar demais, explore séries, músicas e conversas do dia a dia, e use os ebooks extras quando quiser afinar detalhes. Não é sobre subir nível — é sobre manter o inglês vivo.',
  'O inglês já está no seu dia a dia. Siga no Advanced com naturalidade, curta séries e músicas, e recorra aos ebooks quando quiser refinar algo. O foco é manter a língua viva, não evoluir a todo custo.',
  'Você já vive o inglês. Mantenha o Advanced como base, entretenha-se com séries e músicas e use os ebooks quando der vontade de aprimorar. Não é sobre próximo nível — é sobre seguir usando.',
  'O inglês já é parte da sua rotina. Continue no Advanced, aproveite séries, músicas e conversas, e use os ebooks extras quando quiser polir. O objetivo é só manter a língua viva.',
  'Agora é só manter. Use o Advanced com naturalidade, curta séries e músicas, e os ebooks quando quiser afinar. Não é sobre evoluir — é sobre manter o inglês presente.',
  'O inglês já faz parte de você. Siga no Advanced, explore séries e músicas e use os ebooks quando quiser refinar. Não é sobre próximo nível — é sobre manter a língua viva.',
];

// 3 níveis: Básico, Intermediário, Fluente (condições definidas; fallback com dados atuais)
function getConfidenceLevel(data: ProgressData): { label: string; tip: string } {
  const { weeklyStats, advancedUsagePercent, useOnlyAdvanced, asksMeaningsPerSession, daysWith15MinThisWeek } = data;
  const weekMinutes = weeklyStats?.estimatedMinutes ?? 0;
  const weekDays = weeklyStats?.daysActive ?? 0;
  const weekIndex = getWeekOfYear();

  const pickTip = (tips: string[]) => tips[weekIndex % tips.length];

  // Fluente: usa APENAS modo advanced + pelo menos 15 min em pelo menos 5 dias na semana
  if (useOnlyAdvanced && (daysWith15MinThisWeek ?? 0) >= 5) {
    return { label: 'Fluente', tip: pickTip(TIPS_FLUENTE) };
  }

  // Intermediário: usa beginner mas entre 60% e 90% no modo advanced
  if (advancedUsagePercent != null && advancedUsagePercent >= 60 && advancedUsagePercent <= 90) {
    return { label: 'Intermediário', tip: pickTip(TIPS_INTERMEDIARIO) };
  }

  // Básico: 20–50% advanced OU pergunta +3 vezes por sessão significados / "não entendi" / pede explicações
  if (
    (advancedUsagePercent != null && advancedUsagePercent >= 20 && advancedUsagePercent <= 50) ||
    (asksMeaningsPerSession != null && asksMeaningsPerSession > 3)
  ) {
    return { label: 'Básico', tip: pickTip(TIPS_BASICO) };
  }

  // Fallback com dados atuais (até o backend enviar advancedUsagePercent, useOnlyAdvanced, etc.)
  if (weekDays >= 5 && weekMinutes >= 75) {
    return { label: 'Fluente', tip: pickTip(TIPS_FLUENTE) };
  }
  if (weekDays >= 3 && weekMinutes >= 30) {
    return { label: 'Intermediário', tip: pickTip(TIPS_INTERMEDIARIO) };
  }
  return { label: 'Básico', tip: pickTip(TIPS_BASICO) };
}

// Determine next step suggestion (texto antigo; usado só se getNextStepChallengeFallback não cobrir)
function getNextStep(totalMessages: number): string {
  if (totalMessages >= 100) return 'Debater sobre temas atuais.';
  if (totalMessages >= 60) return 'Falar sobre seus planos futuros.';
  if (totalMessages >= 30) return 'Descrever experiências do passado.';
  if (totalMessages >= 15) return 'Contar sobre seus hobbies e interesses.';
  return 'Falar sobre sua rotina diária.';
}

/** Fallback com as mesmas cópias de missão/jogo quando a API não envia nextStepChallenge */
function getNextStepChallengeFallback(
  achievements: string[],
  totalMessages: number
): { title: string; subtitle: string } {
  const has = (label: string) => achievements.includes(label);
  if (!has('Se apresentar em inglês'))
    return { title: 'MISSÃO: Sua primeira apresentação', subtitle: 'Desbloqueie essa skill falando quem você é em inglês. 1 conversa e a missão é sua.' };
  if (!has('Falar sobre sua rotina'))
    return { title: 'MISSÃO: Contar seu dia', subtitle: 'Conte um dia típico seu pro Ataric. Complete e suba de nível na rotina.' };
  if (!has('Falar sobre hobbies e interesses'))
    return { title: 'MISSÃO: Falar do que você curte', subtitle: 'Escolha um hobby e sustente a conversa. Essa missão é prazerosa — vale a pena.' };
  if (!has('Expressar opiniões em inglês'))
    return { title: 'MISSÃO: Dar sua opinião', subtitle: 'Diga o que você acha sobre um tema em inglês. Uma opinião bem dada = missão completa.' };
  if (!has('Descrever experiências do passado'))
    return { title: 'MISSÃO: Contar uma história passada', subtitle: 'Relembre algo que viveu e conte em inglês. Histórias passadas desbloqueiam nível.' };
  if (!has('Falar sobre planos futuros'))
    return { title: 'MISSÃO: Falar dos seus planos', subtitle: 'O que você vai fazer? Conte em inglês e complete essa missão em poucos minutos.' };
  if (!has('Conversar sobre temas variados'))
    return { title: 'MISSÃO: Trocar de tema no meio da conversa', subtitle: 'Puxe um assunto novo e segure a conversa. Quem varia tema sobe de nível.' };
  if (totalMessages >= 50)
    return { title: 'MISSÃO: Aprofundar um tema', subtitle: 'Escolha um assunto que você já domina e vá mais longe. Mais detalhes = mais XP. Aceite a missão?' };
  return { title: 'MISSÃO: Manter o ritmo', subtitle: 'Você já manda bem em vários temas. Repita um que você curte e deixe a fala ainda mais natural. 1 conversa = missão feita.' };
}

function Progress({ data, userName }: ProgressProps) {
  const navigate = useNavigate();
  // Dados reais: conquistas das conversas (backend); fallback para tier por totalMessages
  const achievementsFromApi = data.achievementsFromConversations;
  const achievements: string[] =
    Array.isArray(achievementsFromApi) && achievementsFromApi.length > 0
      ? achievementsFromApi
      : getAchievements(data);
  const confidence = getConfidenceLevel(data);
  // Desafio/incentivo do backend (baseado em dados reais); fallback com as mesmas cópias de missão
  const apiChallenge = data.nextStepChallenge;
  const challenge =
    apiChallenge && typeof apiChallenge.title === 'string' && typeof apiChallenge.subtitle === 'string'
      ? { title: apiChallenge.title, subtitle: apiChallenge.subtitle }
      : getNextStepChallengeFallback(achievements, data.totalMessages);
  const noPracticeThisWeek = data.noPracticeThisWeek === true;
  const weekMinutes = data.weeklyStats?.estimatedMinutes ?? 0;
  const weekDays = data.weeklyStats?.daysActive ?? 0;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0a2e] via-[#16082a] to-[#0f0518] relative overflow-x-hidden">
      <BackgroundTetris />

      {/* Fundo com padrão em anexo, como se tivessem “montado” o progresso no meio */}
      <div className="progress-inner">

        {/* Header: mesmo layout em web e mobile */}
        <motion.header
          className="progress-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex-shrink-0 pt-1">
            <FloatingMascot size="lg" />
          </div>
          <div>
            <p className="progress-header-greeting">Olá,</p>
            <h1 className="progress-header-name">{userName}</h1>
          </div>
        </motion.header>

        {/* O QUE VOCÊ JÁ CONSEGUE */}
        <motion.section
          className="mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-purple-400" />
            <h2 className="progress-section-title">O que você já consegue</h2>
          </div>

          <div className="space-y-3">
            {achievements.map((achievement, index) => (
              <motion.div
                key={achievement}
                className="relative group"
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + index * 0.15, duration: 0.5, type: 'spring' }}
              >
                <motion.div
                  className="flex items-center gap-4 bg-gradient-to-r from-purple-600/30 to-purple-500/10 backdrop-blur-sm rounded-2xl px-5 py-4 border border-purple-500/30 cursor-pointer"
                  whileHover={{
                    scale: 1.03,
                    borderColor: 'rgba(168, 85, 247, 0.6)',
                    boxShadow: '0 0 20px rgba(168, 85, 247, 0.3)',
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <motion.div
                    className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-500 flex items-center justify-center shadow-lg shadow-green-500/30"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.5 + index * 0.15, type: 'spring', stiffness: 200 }}
                  >
                    <Check className="w-5 h-5 text-white" strokeWidth={3} />
                  </motion.div>
                  <span className="text-white font-medium text-lg flex-1">{achievement}</span>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* PRÁTICA DA SEMANA - números em destaque (branco negrito/maior), labels branco sem negrito; metade monstrinho no canto */}
        <motion.section
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
        >
          <h2 className="progress-section-title mb-4">Prática da semana</h2>
          {noPracticeThisWeek && (
            <p className="text-white/70 text-sm mb-3" style={{ fontWeight: 400 }}>
              Você não praticou esta semana — minutos e dias abaixo são da semana atual (dados reais).
            </p>
          )}

          <div className="flex gap-3">
            {/* Card minutos – ícone mic branco em destaque; número maior e negrito branco; texto abaixo branco sem negrito; monstrinho vermelho no canto */}
            <motion.div
              className="flex-1 relative bg-gradient-to-br from-purple-600/40 to-purple-800/40 backdrop-blur-sm rounded-2xl p-5 border border-purple-500/30 overflow-hidden cursor-pointer"
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-purple-500/20 to-transparent"
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <Mic className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                <p className="text-5xl font-bold" style={{ color: '#ffffff' }}>{weekMinutes} min</p>
                <p className="text-base" style={{ color: '#ffffff', fontWeight: 400 }}>falando inglês</p>
              </div>
              <motion.div
                className="absolute bottom-2 right-2 w-7 h-7"
                animate={{ y: [0, 4, 0], rotate: [0, 2, -2, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
              >
                <img
                  src="/images/progress/monster-model.png"
                  alt=""
                  className="w-full h-full object-contain"
                  style={{ filter: 'hue-rotate(90deg) saturate(1.3)' }}
                />
              </motion.div>
            </motion.div>

            {/* Card dias – ícone calendário branco; número maior e negrito branco; texto abaixo branco sem negrito; monstrinho verde (modelo da imagem) */}
            <motion.div
              className="flex-1 relative bg-gradient-to-br from-green-600/40 to-green-800/40 backdrop-blur-sm rounded-2xl p-5 border border-green-500/30 overflow-hidden cursor-pointer"
              whileHover={{ scale: 1.05, y: -5 }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-green-500/20 to-transparent"
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
              />
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-6 h-6 text-white" strokeWidth={2.5} />
                </div>
                <p className="text-5xl font-bold" style={{ color: '#ffffff' }}>{weekDays} {weekDays === 1 ? 'dia' : 'dias'}</p>
                <p className="text-base" style={{ color: '#ffffff', fontWeight: 400 }}>nesta semana</p>
              </div>
              <motion.div
                className="absolute bottom-2 right-2 w-7 h-7"
                animate={{ y: [0, 4, 0], rotate: [0, -2, 2, 0] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut', delay: 0.2 }}
              >
                <img
                  src="/images/progress/monster-model.png"
                  alt=""
                  className="w-full h-full object-contain"
                  style={{ filter: 'hue-rotate(200deg) saturate(1.2)' }}
                />
              </motion.div>
            </motion.div>
          </div>
        </motion.section>

        {/* SUA CONFIANÇA AO FALAR - 3 níveis; rótulos roxo claro negrito; dica em branco */}
        <motion.section
          className="mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.6 }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-purple-400" />
            <h2 className="progress-section-title">Sua confiança ao falar</h2>
          </div>

          <motion.div
            className="relative bg-gradient-to-br from-purple-900/40 to-indigo-900/20 backdrop-blur-sm rounded-2xl p-5 border border-purple-500/30 overflow-hidden"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl" />

            <div className="relative z-10 space-y-4">
              <p className="text-lg font-bold tracking-wide" style={{ color: '#b076d4', textShadow: '0 0 12px rgba(176, 118, 212, 0.4)' }}>Seu inglês hoje está em:</p>

              <motion.div
                className="flex items-center gap-3"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9, type: 'spring' }}
              >
                <div className="w-3 h-3 rounded-full bg-gradient-to-br from-green-400 to-green-500 shadow-lg shadow-green-500/30" />
                <span className="text-2xl font-bold text-white">{confidence.label}</span>
              </motion.div>

              <div className="pt-3 border-t border-white/10 space-y-2">
                <p className="text-lg font-bold tracking-wide flex items-center gap-2" style={{ color: '#b076d4', textShadow: '0 0 12px rgba(176, 118, 212, 0.4)' }}>
                  <span className="text-lg">💡</span>
                  Dica para desbloquear mais confiança:
                </p>
                <p className="text-sm leading-relaxed" style={{ color: '#ffffff', fontWeight: 400 }}>{confidence.tip}</p>
              </div>
            </div>
          </motion.div>
        </motion.section>

        {/* PRÓXIMO PASSO / DESAFIO — baseado em dados reais (backend); estilo game */}
        <motion.section
          className="mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6 }}
        >
          <motion.div
            className="relative bg-gradient-to-br from-purple-900/50 to-indigo-900/30 backdrop-blur-sm rounded-3xl p-6 pb-4 border-2 border-dashed border-purple-500/40 overflow-hidden group cursor-pointer min-h-[140px]"
            whileHover={{ borderColor: 'rgba(168, 85, 247, 0.7)', scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {/* Animated stars background */}
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(6)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-1 h-1 bg-white rounded-full"
                  style={{
                    left: `${15 + i * 15}%`,
                    top: `${20 + (i % 3) * 25}%`,
                  }}
                  animate={{
                    opacity: [0.2, 1, 0.2],
                    scale: [0.8, 1.2, 0.8],
                  }}
                  transition={{
                    duration: 1.5 + i * 0.3,
                    repeat: Infinity,
                    delay: i * 0.2,
                  }}
                />
              ))}
            </div>

            {/* Foguete com monstrinho (imagem) — animação de decolagem */}
            <motion.div
              className="absolute right-0 bottom-0 flex items-end justify-end"
              initial={false}
              animate={{
                y: [0, -12, 0],
                rotate: [-2, 2, -2],
                scale: [1, 1.02, 1],
              }}
              transition={{
                duration: 2.2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
              whileHover={{ scale: 1.08, y: -4 }}
            >
              <img
                src="/images/progress/rocket-monster.png"
                alt="Monstrinho decolando no foguete"
                className="h-24 w-auto object-contain drop-shadow-xl object-bottom md:h-28"
                draggable={false}
              />
            </motion.div>

            <div className="pr-20 relative z-10">
              <p
                className="text-2xl md:text-3xl uppercase tracking-wider mb-2"
                style={{ color: '#ffffff', fontWeight: 700 }}
              >
                {challenge.title}
              </p>
              <h3 className="text-white text-xl font-bold">{challenge.subtitle}</h3>
            </div>
          </motion.div>
        </motion.section>

        {/* Share CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
        >
          <ProgressButton
            variant="cta"
            size="xl"
            className="w-full bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 shadow-lg shadow-purple-500/30"
            onClick={() => navigate('/compartilhar')}
          >
            <Share2 className="w-5 h-5 mr-2" />
            Compartilhar meu progresso
          </ProgressButton>
        </motion.div>
      </div>
    </div>
  );
}

export default Progress;
